import React from "react";
import { useGame } from "@/contexts/GameContext";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  ShoppingCart,
  Flame,
  Gift,
  MessagesSquare, // Zmieniono na MessagesSquare
  ShieldOff,
  Star,
  Zap,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { motion } from "framer-motion";
import { cn } from "@/lib/utils";
import { AnimatedGroup } from "../ui/animated-group";

// Przykładowe dane sklepu (w przyszłości mogą pochodzić z API)
const shopItems = [
  {
    id: "pack_nsfw_plus",
    type: "pack",
    name: "Pakiet +18 Extreme",
    description: "Najgorętsze pytania i wyzwania tylko dla dorosłych.",
    price: "9,99 PLN",
    icon: Flame,
    unlocked: false,
    color: "bg-red-500/10 border-red-500/30 text-red-500",
    buttonColor: "bg-red-600 hover:bg-red-700",
  },
  {
    id: "pack_birthday_bash",
    type: "pack",
    name: "Impreza Urodzinowa",
    description: "Idealne zadania na szalone świętowanie urodzin.",
    price: "7,99 PLN",
    icon: Gift,
    unlocked: false,
    color: "bg-yellow-500/10 border-yellow-500/30 text-yellow-500",
    buttonColor: "bg-yellow-600 hover:bg-yellow-700",
  },
  {
    id: "pack_icebreaker",
    type: "pack",
    name: "Lodołamacze Pro",
    description: "Poznajcie się lepiej niż kiedykolwiek.",
    price: "5,99 PLN",
    icon: MessagesSquare,
    unlocked: true, // Przykład odblokowanego
    color: "bg-sky-500/10 border-sky-500/30 text-sky-500",
    buttonColor: "bg-sky-600 hover:bg-sky-700",
  },
  {
    id: "pack_couples",
    type: "pack",
    name: "Dla Par",
    description: "Pikantne i romantyczne zadania dla dwojga.",
    price: "8,99 PLN",
    icon: Star, // Zmieniono ikonę
    unlocked: false,
    color: "bg-pink-500/10 border-pink-500/30 text-pink-500",
    buttonColor: "bg-pink-600 hover:bg-pink-700",
  },
  {
    id: "feature_no_ads",
    type: "feature",
    name: "Bez Reklam",
    description: "Czysta zabawa, zero przerw i wsparcie dla twórców.",
    price: "14,99 PLN",
    icon: ShieldOff,
    unlocked: false,
    color: "bg-green-500/10 border-green-500/30 text-green-500",
    buttonColor: "bg-green-600 hover:bg-green-700",
  },
  {
    id: "feature_more_skips",
    type: "feature",
    name: "Więcej Pomiń",
    description: "Dodatkowe 5 pominięć dla każdego gracza.",
    price: "4,99 PLN",
    icon: Zap,
    unlocked: false,
    color: "bg-purple-500/10 border-purple-500/30 text-purple-500",
    buttonColor: "bg-purple-600 hover:bg-purple-700",
  },
];

export default function ShopScreen() {
  const { dispatch } = useGame();

  const goBack = () => {
    dispatch({ type: "SET_SCREEN", payload: "start" });
  };

  const handleBuy = (item) => {
    // TODO: Zaimplementuj logikę zakupu (np. In-App Purchases)
    console.log("Kupowanie:", item.name);
    alert(`Implementacja zakupu dla "${item.name}" jest potrzebna!`);
  };

  return (
    <>
      {/* Header */}
      <header className="flex items-center justify-between mb-8">
        <Button
          variant="ghost"
          size="icon"
          onClick={goBack}
          className="rounded-full"
        >
          <ArrowLeft className="size-5" />
        </Button>
        <h1 className="text-xl font-semibold">Sklep (w budowie)</h1>
        <div className="w-9 h-9" /> {/* Spacer */}
      </header>

      <main className="flex-1 space-y-6 pb-10">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-foreground">
            Odblokuj Nowe Pakiety i Funkcje!
          </h2>
          <p className="text-muted-foreground mt-1">
            Wzbogać swoją grę o nowe, ekscytujące możliwości.
          </p>
        </div>

        {/* Lista produktów */}
        <AnimatedGroup className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {shopItems.map((item) => {
            const Icon = item.icon;
            return (
              <Card
                key={item.id}
                className={cn(
                  "flex flex-col h-full overflow-hidden transition-all duration-200 hover:shadow-lg hover:shadow-primary/10 border",
                  item.color
                )}
              >
                <CardHeader className="flex-row items-center gap-4 space-y-0 p-4">
                  {" "}
                  {/* Zmieniono layout nagłówka */}
                  <div
                    className={cn(
                      "p-3 rounded-lg",
                      item.color.replace("/10", "/20")
                    )}
                  >
                    <Icon
                      className={cn(
                        "size-6",
                        item.color
                          .replace("bg-", "")
                          .replace("/10", "")
                          .replace(" border-", "")
                          .replace("/30", "")
                      )}
                    />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base font-bold">
                      {item.name}
                    </CardTitle>
                    <CardDescription className="text-xs line-clamp-2">
                      {item.description}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="p-4 pt-0 flex-1">
                  {/* Można dodać więcej szczegółów lub obrazek */}
                </CardContent>
                <CardFooter className="p-4 pt-0">
                  {item.unlocked ? (
                    <Button disabled variant="outline" className="w-full">
                      Odblokowano
                    </Button>
                  ) : (
                    <Button
                      disabled
                      onClick={() => handleBuy(item)}
                      className={cn("w-full text-white", item.buttonColor)}
                    >
                      Kup za {item.price}
                    </Button>
                  )}
                </CardFooter>
              </Card>
            );
          })}
        </AnimatedGroup>

        <div className="text-center mt-8">
          <p className="text-xs text-muted-foreground">
            Płatności są przetwarzane przez bezpieczne systemy sklepów (Google
            Play / App Store).
            <br /> Dziękujemy za wsparcie rozwoju aplikacji!
          </p>
        </div>
      </main>
    </>
  );
}
